import { track as trackEvent } from "@/lib/analytics"
import { GUEST_BASE, isGuestThread, isLoginRequired, requireLogin } from "@/lib/authGate"
import { lastTurnRequestId, withReference } from "@/lib/requestIds"
import { followTurn, getThread, listDocuments, startTurnWithResume } from "../api"
import { contextFrom, sideWords, stateName, useReaderContext } from "../readerContext"
import { useDocsStore } from "../docs.store"
import type { RouteView, TurnMode, TurnRequest } from "../contract"
import { emptyTurn, useTurnStore, type TurnView } from "../store"
import { NiraHttpError, type StreamEvent } from "../stream"
import { useAnswerSession, type TurnKind } from "./session"
import { caseOf, isActive, parseThread, supersededIds } from "./thread"

export const BUSY_NOTICE = "NIRA is still answering in this thread. Showing that answer now."
const CLOSED = "The connection closed before NIRA finished."
const UNREACHABLE = "NIRA could not reach the server."
const UNFINISHED = "This answer did not finish."

const controllers = new Set<AbortController>()
/** When each locally started turn began, for `answer_completed.duration_ms`. Reloaded or reattached turns are not measured. */
const startedAt = new Map<string, number>()
let counter = 0

/** Stop every stream this page started (unmount or thread change). */
export function abortAll(): void {
  controllers.forEach((c) => c.abort())
  controllers.clear()
}

function track(): AbortController {
  const c = new AbortController()
  controllers.add(c)
  return c
}

const statusOf = (err: unknown): number | undefined => {
  const s = (err as { status?: unknown } | null)?.status
  return typeof s === "number" ? s : undefined
}

/** Feed one event into the turn store, remembering when each step first appeared. */
function record(key: string, ev: StreamEvent, threadId?: string): void {
  const p = ev.payload
  // The turn reads for the context the server settled on (the State may have been guessed).
  if (ev.type === "turn.started" && threadId) {
    const ctx = contextFrom(p)
    if (ctx) {
      useReaderContext.getState().setThread(threadId, ctx)
      useAnswerSession.getState().patchMeta(key, { readerAt: ctx })
    }
    // A turn sent from this tab: the id its POST carried is the one telemetry (and feedback) joins on.
    const requestId = lastTurnRequestId(threadId)
    if (requestId && useAnswerSession.getState().meta[key]?.live) useAnswerSession.getState().patchMeta(key, { requestId })
  }
  if (ev.type === "step" && typeof p.id === "string" && typeof p.t === "number") {
    useAnswerSession.getState().noteStep(key, p.id, p.t)
  }
  useTurnStore.getState().apply(key, ev)
  trackOutcome(key, ev)
}

/** One `answer_completed` or `answer_failed` per locally started turn: counts, mode and codes only. */
function trackOutcome(key: string, ev: StreamEvent): void {
  const t0 = startedAt.get(key)
  if (t0 === undefined) return
  const view = useTurnStore.getState().turns[key]
  if (ev.type === "answer.done" && view) {
    startedAt.delete(key)
    trackEvent("answer_completed", {
      mode: view.mode,
      duration_ms: Date.now() - t0,
      sources_count: Object.keys(view.sources).length,
      status: view.limits.length > 0 ? "partial" : "ok",
    })
  } else if (ev.type === "turn.error") {
    startedAt.delete(key)
    const code = ev.payload.code
    trackEvent("answer_failed", { code: typeof code === "string" && code ? code : "unknown" })
  }
}

function seed(key: string, view: TurnView): void {
  useTurnStore.setState((s) => ({
    turns: { ...s.turns, [key]: view },
    order: s.order.includes(key) ? s.order : [...s.order, key],
  }))
}

function removeTurn(key: string): void {
  // A turn that never started does not replace the one it was meant to re-run.
  const { meta, patchMeta } = useAnswerSession.getState()
  for (const [k, m] of Object.entries(meta)) if (m.supersededBy === key) patchMeta(k, { supersededBy: undefined })
  useTurnStore.setState((s) => {
    const turns = { ...s.turns }
    delete turns[key]
    return { turns, order: s.order.filter((k) => k !== key) }
  })
}

function fail(key: string, message: string): void {
  const view = useTurnStore.getState().turns[key]
  record(key, {
    type: "turn.error",
    payload: { code: "internal", message, retryable: true, partial: !!view?.head, mascot: "error" },
  })
}

/** A stream that ended while the turn still reads as running gets a calm, retryable error. */
function settle(key: string): void {
  const view = useTurnStore.getState().turns[key]
  if (view && view.status === "running") fail(key, CLOSED)
}

/** Reattach to the thread's active run. Returns how many events arrived (0 means none was running). */
async function reattach(threadId: string, preferred: string | undefined, signal: AbortSignal): Promise<number> {
  let key = preferred
  let n = 0
  const session = useAnswerSession.getState()
  const sink = (ev: StreamEvent) => {
    n += 1
    if (!key) {
      const id = ev.type === "turn.started" ? ev.payload.turn_id : undefined
      key = typeof id === "string" && id ? id : `live-${++counter}`
      if (!useAnswerSession.getState().meta[key]) {
        const mode = (ev.payload.mode === "draft_check" ? "draft_check" : "ask") as TurnMode
        session.setMeta(key, {
          request: { message: String(ev.payload.question ?? ""), mode, doc_ids: [] },
          kind: "ask",
          live: true,
        })
      }
    } else if (n === 1) {
      useAnswerSession.getState().patchMeta(key, { live: true })
    }
    record(key, ev, threadId)
  }
  if (isGuestThread(threadId)) await followTurn(threadId, sink, signal, GUEST_BASE)
  else await followTurn(threadId, sink, signal)
  if (key && n > 0) settle(key)
  return n
}

/** Start a new turn and stream it into the store. A 409 shows a notice and reattaches instead. */
export async function sendTurn(
  threadId: string,
  request: TurnRequest,
  kind: TurnKind = "ask",
  summary?: string,
  /** The turn this one re-runs: it folds to a stub once this one starts. */
  supersedes?: string,
): Promise<string> {
  const key = `local-${Date.now().toString(36)}-${++counter}`
  const session = useAnswerSession.getState()
  session.setMeta(key, { request, kind, summary, live: true })
  if (supersedes) session.patchMeta(supersedes, { supersededBy: key })
  seed(key, { ...emptyTurn(key), question: request.message, mode: request.mode })
  trackEvent("search_submitted", { mode: request.mode })
  startedAt.set(key, Date.now())
  const c = track()
  try {
    const onEvent = (ev: StreamEvent) => record(key, ev, threadId)
    // Guests send exactly {message, mode: "ask"}: no documents and no draft check.
    const guestBody = { message: request.message, mode: "ask" } as unknown as TurnRequest
    if (isGuestThread(threadId)) await startTurnWithResume(threadId, guestBody, onEvent, c.signal, undefined, GUEST_BASE)
    else await startTurnWithResume(threadId, request, onEvent, c.signal)
    settle(key)
  } catch (err) {
    if (c.signal.aborted) return key
    if (isLoginRequired(err)) {
      removeTurn(key)
      requireLogin("login_required", "/ask")
    } else if (err instanceof NiraHttpError && err.code === "route" && err.body && typeof err.body === "object") {
      // A notice or an order sent as a question: its own door, not a failed answer.
      removeTurn(key)
      const route = (err.body as { route?: RouteView }).route
      if (route) useAnswerSession.getState().setRoute({ ...route, question: request.message })
      trackEvent("research_routed", { to: route?.to ?? "", why: route?.why ?? "", from: "answer" })
    } else if (statusOf(err) === 409) {
      removeTurn(key)
      useAnswerSession.getState().setNotice(BUSY_NOTICE)
      try {
        await reattach(threadId, undefined, c.signal)
      } catch {
        /* the notice stays; the user can ask again once the run ends */
      }
      if (!c.signal.aborted) useAnswerSession.getState().setNotice(null)
    } else {
      fail(key, withReference(UNREACHABLE, err))
    }
  } finally {
    controllers.delete(c)
    startedAt.delete(key)
  }
  return key
}

/** True while any turn in the thread is still being answered (a new send would 409). */
export function threadBusy(): boolean {
  return Object.values(useTurnStore.getState().turns).some((v) => v.status === "running")
}

/** Re-send a turn's original request as a new turn that replaces it. Refused while a turn runs. */
export function retryTurn(threadId: string, key: string): Promise<string> | undefined {
  const meta = useAnswerSession.getState().meta[key]
  if (!meta || threadBusy()) return undefined
  useAnswerSession.getState().patchMeta(key, { retried: true })
  return sendTurn(threadId, meta.request, meta.kind, meta.summary, key)
}

/** The turn that stands: the newest one that is not folded under a re-run. */
export function effectiveTurnKey(): string | undefined {
  const { order } = useTurnStore.getState()
  const { meta } = useAnswerSession.getState()
  return [...order].reverse().find((k) => !meta[k]?.supersededBy)
}

/** Whether the latest turn can be re-run with a new context (it has finished and nothing is running). */
export function canRerun(): boolean {
  const key = effectiveTurnKey()
  const view = key ? useTurnStore.getState().turns[key] : undefined
  return !!key && !!useAnswerSession.getState().meta[key] && (view?.status === "done" || view?.status === "error")
}

/** Re-send the latest question under the thread's current State and side; it replaces that turn. */
export function rerunLatest(threadId: string): Promise<string> | undefined {
  const key = effectiveTurnKey()
  const meta = key ? useAnswerSession.getState().meta[key] : undefined
  if (!key || !meta || threadBusy() || !canRerun()) return undefined
  const r = useReaderContext.getState().threads[threadId]
  const summary = r ? `${r.state || stateName(r.state_code)} · ${sideWords(r.side)}` : undefined
  return sendTurn(threadId, meta.request, "context", summary, key)
}

/** Reload: render stored turns, then reattach to a run that is still going (204 means none). */
export async function hydrateThread(threadId: string): Promise<void> {
  const c = track()
  try {
    const data = await (isGuestThread(threadId) ? getThread(threadId, GUEST_BASE) : getThread(threadId))
    const { turns, title } = parseThread(data)
    if (c.signal.aborted) return
    const ctx = contextFrom(data)
    if (ctx) useReaderContext.getState().setThread(threadId, ctx)
    useAnswerSession.getState().setTitle(title)
    useAnswerSession.getState().setCaseFile(caseOf(data))
    const map: Record<string, TurnView> = {}
    const session = useAnswerSession.getState()
    for (const t of turns) {
      const view = t.view ?? { ...emptyTurn(t.turnId), question: t.question, status: "running" as const }
      map[t.turnId] = view
      session.setMeta(t.turnId, {
        request: { message: t.question, mode: view.mode === "draft_check" ? "draft_check" : "ask", doc_ids: t.docIds },
        kind: "ask",
        live: false,
      })
    }
    // The server keeps every run of a question; only the last of a repeated question stands.
    for (const id of supersededIds(turns)) session.patchMeta(id, { supersededBy: "later" })
    useTurnStore.setState({ turns: map, order: turns.map((t) => t.turnId) })
    if (turns.some((t) => t.docIds.length)) {
      listDocuments(threadId)
        .then((rows) => !c.signal.aborted && Array.isArray(rows) && useDocsStore.getState().setRows(rows))
        .catch(() => undefined)
    }
    const active = [...turns].reverse().find(isActive)
    for (const t of turns) if (!t.view && t !== active) fail(t.turnId, UNFINISHED)
    const n = await reattach(threadId, active?.turnId, c.signal)
    if (active && n === 0 && !c.signal.aborted) fail(active.turnId, UNFINISHED)
  } catch (err) {
    if (c.signal.aborted) return
    if (isLoginRequired(err)) return requireLogin("login_required", "/ask")
    useAnswerSession.getState().setNotice(
      withReference(statusOf(err) === 404 ? "This thread was not found." : "NIRA could not load this thread.", err),
    )
  } finally {
    controllers.delete(c)
  }
}
